/**
 * Product Template - Main Component
 * BEM Methodology
 * Manages product media slider and form collapse
 */

class ProductTemplate {
  constructor(element) {
    this.element = element;
    this.mediaElement = null;
    this.collapseElement = null;
    this.upsellsElement = null;
    this.videosElement = null;
    this.mainSlider = null;
    this.thumbsSlider = null;
    this.videoSlider = null;
    this.collapseItems = [];
    this.upsellsState = [];
    this.init();
  }

  async init() {
    this.mediaElement = this.element.querySelector('[data-product-media]');
    this.collapseElement = this.element.querySelector('[data-product-collapse]');
    this.upsellsElement = this.element.querySelector('[data-product-upsells]');
    this.packElement = this.element.querySelector('[data-product-pack]');
    this.subscriptionElement = this.element.querySelector('[data-product-subscription]');
    this.videosElement = this.element.querySelector('[data-product-videos]');

    // Store base product price
    const submitElement = this.element.querySelector('[data-product-submit]');
    const submitPriceText = submitElement?.querySelector('[data-submit-price]')?.textContent;
    let fallbackPrice = null;
    if (submitPriceText) {
      const priceMatch = submitPriceText.match(/[\d,]+/);
      if (priceMatch) {
        fallbackPrice = Math.round(parseFloat(priceMatch[0].replace(',', '.')) * 100);
      }
    }

    this.baseProductPrice = this.packElement?.dataset.productBasePrice ||
      this.element.querySelector('[data-product-quantity]')?.dataset.productBasePrice ||
      fallbackPrice;

    // Store base compare at price
    const submitPriceElement = submitElement?.querySelector('[data-submit-price]');
    const compareAtPrice = submitPriceElement?.dataset.compareAtPrice;
    this.baseCompareAtPrice = compareAtPrice ? parseInt(compareAtPrice) : null;

    // Initialize media slider if exists
    if (this.mediaElement) {
      await this.loadSwiper();
      this.initializeSliders();
    }

    // Initialize collapse if exists
    if (this.collapseElement) {
      this.initializeCollapse();
    }

    // Initialize upsells if exists
    if (this.upsellsElement) {
      this.initializeUpsells();
    }

    // Initialize pack dropdown if exists
    if (this.packElement) {
      this.initializePackDropdown();
    }

    // Initialize subscription if exists
    if (this.subscriptionElement) {
      this.initializeSubscription();
    }

    // Initialize videos if exists
    if (this.videosElement) {
      await this.loadSwiper();
      this.initializeVideos();
    }

    // Initialize submit form if exists
    this.submitElement = this.element.querySelector('[data-product-submit]');
    if (this.submitElement) {
      this.initializeSubmit();
    }

    // Initialize quantity selector if exists
    this.quantityElement = this.element.querySelector('[data-product-quantity]');
    if (this.quantityElement) {
      this.initializeQuantitySelector();
    }

    // Initialize form submit handler (must be last)
    this.initializeFormSubmit();

    // Initialize sticky bar if exists
    this.stickyBarElement = document.querySelector('[data-product-sticky-bar]');
    if (this.stickyBarElement) {
      this.initializeStickyBar();
    }

    // Initialize low stock indicator if exists
    this.lowStockElement = this.element.querySelector('[data-product-low-stock]');
    if (this.lowStockElement) {
      this.initializeLowStockIndicator();
    }

    // Initialize smooth scroll for anchor links
    this.initializeAnchorLinks();
  }

  // ========================================
  // Media Slider Methods
  // ========================================

  async loadSwiper() {
    // Check if Swiper is already loaded
    if (typeof Swiper !== 'undefined') {
      return Promise.resolve();
    }

    // Load Swiper CSS
    const swiperCSS = document.createElement('link');
    swiperCSS.rel = 'stylesheet';
    swiperCSS.href =
      'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
    document.head.appendChild(swiperCSS);

    // Load Swiper JS
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src =
        'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  initializeSliders() {
    const mainSliderElement = this.mediaElement.querySelector(
      '[data-main-slider]'
    );
    const thumbsSliderElement = this.mediaElement.querySelector(
      '[data-thumbs-slider]'
    );

    if (!mainSliderElement) return;

    // Initialize thumbnail slider first (if exists)
    if (thumbsSliderElement) {
      this.thumbsSlider = new Swiper(thumbsSliderElement, {
        spaceBetween: 0, // Gap is handled by CSS
        slidesPerView: 'auto',
        freeMode: true,
        watchSlidesProgress: true,
        centeredSlides: false,
      });
    }

    // Initialize main slider
    const hasNavigation = this.mediaElement.querySelector(
      '.w-product-media__navigation'
    );
    const enableZoom = this.mediaElement.querySelector('.w-product-media__main')?.dataset.enableZoom === 'true';

    const mainSliderConfig = {
      spaceBetween: 0,
      slidesPerView: 1,
      navigation: hasNavigation
        ? {
          nextEl: '.w-product-media__nav--next',
          prevEl: '.w-product-media__nav--prev',
        }
        : false,
      thumbs: this.thumbsSlider
        ? {
          swiper: this.thumbsSlider,
        }
        : undefined,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      loop: false,
      grabCursor: true,
      effect: 'slide',
      speed: 300,
      breakpoints: {
        990: {
          spaceBetween: 24,
        },
      },
    };

    this.mainSlider = new Swiper(mainSliderElement, mainSliderConfig);

    // Handle zoom functionality
    if (enableZoom) {
      this.initializeZoom();
    }

    // Sync sliders on variant change
    this.observeVariantChange();
  }

  initializeZoom() {
    const slides = this.mediaElement.querySelectorAll(
      '.w-product-media__slide-inner'
    );

    slides.forEach((slide) => {
      const image = slide.querySelector('.w-product-media__image');
      if (!image) return;

      slide.addEventListener('click', (e) => {
        if (image.classList.contains('zoomed')) {
          this.resetZoom(slide, image);
        } else {
          this.zoomImage(slide, image, e);
        }
      });

      slide.addEventListener('mousemove', (e) => {
        if (image.classList.contains('zoomed')) {
          this.updateZoomPosition(slide, image, e);
        }
      });

      slide.addEventListener('mouseleave', () => {
        if (image.classList.contains('zoomed')) {
          this.resetZoom(slide, image);
        }
      });
    });
  }

  zoomImage(slide, image, event) {
    image.classList.add('zoomed');
    slide.style.cursor = 'zoom-out';
    image.style.transform = 'scale(2)';
    this.updateZoomPosition(slide, image, event);
  }

  resetZoom(slide, image) {
    image.classList.remove('zoomed');
    slide.style.cursor = 'zoom-in';
    image.style.transform = 'scale(1)';
    image.style.transformOrigin = 'center center';
  }

  updateZoomPosition(slide, image, event) {
    const rect = slide.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    image.style.transformOrigin = `${x}% ${y}%`;
  }

  observeVariantChange() {
    // Listen for Shopify variant change events
    document.addEventListener('variant:change', (event) => {
      const { variant } = event.detail;
      if (variant && variant.featured_media) {
        this.goToMedia(variant.featured_media.id);
      }
    });
  }

  goToMedia(mediaId) {
    const slides = this.mediaElement.querySelectorAll('.w-product-media__slide');
    slides.forEach((slide, index) => {
      const media = slide.querySelector('[data-media-id]');
      if (media && media.dataset.mediaId === mediaId.toString()) {
        if (this.mainSlider) {
          this.mainSlider.slideTo(index);
        }
      }
    });
  }

  // ========================================
  // Collapse Methods
  // ========================================

  initializeCollapse() {
    const triggers = this.collapseElement.querySelectorAll(
      '[data-collapse-trigger]'
    );

    triggers.forEach((trigger) => {
      const item = trigger.closest('[data-collapse-item]');
      const content = item.querySelector('[data-collapse-content]');

      this.collapseItems.push({ item, trigger, content });

      // Add click event listener
      trigger.addEventListener('click', () => {
        this.toggleCollapseItem(item);
      });
    });

    // Initialize "see more" button for benefits
    this.initializeBenefitsToggle();
  }

  initializeBenefitsToggle() {
    const benefitsToggle = this.collapseElement?.querySelector('[data-benefits-toggle]');

    if (!benefitsToggle) return;

    benefitsToggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const benefitsList = this.collapseElement.querySelector('[data-benefits-list]');
      if (!benefitsList) return;

      const hiddenBenefits = benefitsList.querySelectorAll('.product-form-collapse__bullet--hidden');
      const isExpanded = hiddenBenefits.length === 0;
      const moreText = benefitsToggle.querySelector('[data-more-text]');

      if (isExpanded) {
        // Hide items beyond the 3rd
        const allBenefits = benefitsList.querySelectorAll('.product-form-collapse__bullet');
        allBenefits.forEach((benefit, index) => {
          if (index >= 3) {
            benefit.classList.add('product-form-collapse__bullet--hidden');
          }
        });
        if (moreText) {
          moreText.textContent = moreText.getAttribute('data-original-text') || 'Voir plus';
        }
      } else {
        // Show all items
        hiddenBenefits.forEach((benefit) => {
          benefit.classList.remove('product-form-collapse__bullet--hidden');
        });
        if (moreText) {
          // Store original text
          if (!moreText.getAttribute('data-original-text')) {
            moreText.setAttribute('data-original-text', moreText.textContent);
          }
          moreText.textContent = 'Voir moins';
        }
      }

      // No need to recalculate - CSS handles the height automatically
    });
  }

  toggleCollapseItem(selectedItem) {
    const isActive = selectedItem.classList.contains(
      'product-form-collapse__item--active'
    );

    // Close all items
    this.collapseItems.forEach(({ item, trigger, content }) => {
      item.classList.remove('product-form-collapse__item--active');
      trigger.setAttribute('aria-expanded', 'false');
      // Remove inline styles to let CSS handle it
      content.style.maxHeight = '';
    });

    // If the clicked item wasn't active, open it
    if (!isActive) {
      selectedItem.classList.add('product-form-collapse__item--active');
      const selectedTrigger = selectedItem.querySelector(
        '[data-collapse-trigger]'
      );

      selectedTrigger.setAttribute('aria-expanded', 'true');
    }
  }

  // ========================================
  // Upsells Methods
  // ========================================

  initializeUpsells() {
    const checkboxes = this.upsellsElement.querySelectorAll('[data-upsell-checkbox]');
    const items = this.upsellsElement.querySelectorAll('[data-upsell-item]');
    const addButton = this.upsellsElement.querySelector('[data-upsell-add-button]');
    const buttonText = this.upsellsElement.querySelector('[data-upsell-button-text]');
    const savingsElement = this.upsellsElement.querySelector('[data-upsell-savings]');
    const savingsText = this.upsellsElement.querySelector('[data-upsell-savings-text]');

    // Get text settings from data attributes
    this.upsellsTexts = {
      buttonDefault: this.upsellsElement.dataset.buttonText || 'Add selected products',
      buttonSingular: this.upsellsElement.dataset.buttonTextSingular || 'Add 1 product',
      buttonPlural: this.upsellsElement.dataset.buttonTextPlural || 'Add [count] products',
      buttonLoading: this.upsellsElement.dataset.buttonLoadingText || 'Adding...',
      buttonSuccess: this.upsellsElement.dataset.buttonSuccessText || 'Added!',
      buttonError: this.upsellsElement.dataset.buttonErrorText || 'Error - Try again',
      savings: this.upsellsElement.dataset.savingsText || 'You save [amount]'
    };

    // Initialize state for each product
    items.forEach((item, index) => {
      const productId = item.dataset.productId;
      const price = parseFloat(item.dataset.productPrice) || 0;
      const comparePrice = parseFloat(item.dataset.productComparePrice) || 0;
      const unavailable = item.dataset.upsellUnavailable === 'true';

      this.upsellsState.push({
        index,
        productId,
        price,
        comparePrice,
        unavailable,
        selected: false,
        checkbox: checkboxes[index],
        item: item
      });
    });

    // Add click event listeners to checkboxes
    checkboxes.forEach((checkbox, index) => {
      checkbox.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleUpsellProduct(index);
      });
    });

    // Add click event listener to add button
    if (addButton) {
      addButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addSelectedUpsells();
      });
    }

    // Initial update of UI
    this.updateUpsellsUI();

    // Sync checkbox heights with items
    this.syncUpsellHeights();

    // Re-sync on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.syncUpsellHeights();
      }, 100);
    });
  }

  syncUpsellHeights() {
    if (!this.upsellsElement) return;

    const checkboxWrappers = this.upsellsElement.querySelectorAll('.product-form-upsells__checkbox-wrapper');
    const items = this.upsellsElement.querySelectorAll('.product-form-upsells__item');

    // Reset heights first
    checkboxWrappers.forEach(wrapper => {
      wrapper.style.height = 'auto';
    });

    // Sync each checkbox wrapper with its corresponding item
    items.forEach((item, index) => {
      if (checkboxWrappers[index]) {
        const itemHeight = item.offsetHeight;
        checkboxWrappers[index].style.height = `${itemHeight}px`;
      }
    });
  }

  toggleUpsellProduct(index) {
    if (!this.upsellsState[index]) return;
    if (this.upsellsState[index].unavailable) return;

    // Toggle selection state
    this.upsellsState[index].selected = !this.upsellsState[index].selected;

    // Update UI
    this.updateUpsellsUI();
  }

  updateUpsellsUI() {
    const addButton = this.upsellsElement.querySelector('[data-upsell-add-button]');
    const buttonText = this.upsellsElement.querySelector('[data-upsell-button-text]');
    const savingsElement = this.upsellsElement.querySelector('[data-upsell-savings]');
    const savingsText = this.upsellsElement.querySelector('[data-upsell-savings-text]');

    let totalPrice = 0;
    let totalComparePrice = 0;
    let selectedCount = 0;

    // Update each product's UI and calculate totals
    this.upsellsState.forEach((product) => {
      const { checkbox, item, selected, price, comparePrice } = product;

      // Update checkbox state
      if (checkbox) {
        checkbox.setAttribute('aria-pressed', selected ? 'true' : 'false');
      }

      // Update item active state
      if (item) {
        if (selected) {
          item.classList.remove('product-form-upsells__item--inactive');
          totalPrice += price;
          totalComparePrice += comparePrice || price;
          selectedCount++;
        } else {
          item.classList.add('product-form-upsells__item--inactive');
        }
      }
    });

    // Update button state
    if (addButton && buttonText) {
      if (selectedCount === 0) {
        addButton.disabled = true;
        buttonText.textContent = this.upsellsTexts.buttonDefault;
      } else {
        addButton.disabled = false;
        // Format total price with Shopify money format
        const formattedTotal = this.formatMoney(totalPrice);

        // Use appropriate text template based on count
        let text;
        if (selectedCount === 1) {
          text = this.upsellsTexts.buttonSingular;
        } else {
          text = this.upsellsTexts.buttonPlural.replace('[count]', selectedCount);
        }

        // Add price to button text
        buttonText.textContent = `${text} - ${formattedTotal}`;
      }
    }

    // Update savings display
    if (savingsElement && savingsText) {
      const savings = totalComparePrice - totalPrice;
      if (savings > 0 && selectedCount > 0) {
        const formattedSavings = this.formatMoney(savings);
        const savingsTextContent = this.upsellsTexts.savings.replace('[amount]', formattedSavings);
        savingsText.textContent = savingsTextContent;
        savingsElement.style.display = 'block';
      } else {
        savingsElement.style.display = 'none';
      }
    }
  }

  async addSelectedUpsells() {
    const selectedProducts = this.upsellsState.filter((product) => product.selected);

    if (selectedProducts.length === 0) return;

    const addButton = this.upsellsElement.querySelector('[data-upsell-add-button]');
    const buttonTextElement = addButton ? addButton.querySelector('[data-upsell-button-text]') : null;

    // Show loading state
    if (addButton && buttonTextElement) {
      addButton.disabled = true;
      buttonTextElement.textContent = this.upsellsTexts.buttonLoading;
    }

    try {
      // Prepare items array
      const items = selectedProducts.map(product => ({
        id: parseInt(product.productId),
        quantity: 1
      }));

      // Add items to cart using the same method as the main form
      await this.addItemsToCart(items);

      // Reset selections
      this.upsellsState.forEach((product) => {
        product.selected = false;
      });

      this.updateUpsellsUI();

      // Show success message
      if (addButton && buttonTextElement) {
        buttonTextElement.textContent = this.upsellsTexts.buttonSuccess;
        setTimeout(() => {
          this.updateUpsellsUI();
        }, 2000);
      }
    } catch (error) {
      console.error('Error adding upsells to cart:', error);

      // Show error message
      if (addButton && buttonTextElement) {
        addButton.disabled = false;
        buttonTextElement.textContent = this.upsellsTexts.buttonError;
        setTimeout(() => {
          this.updateUpsellsUI();
        }, 2000);
      }
    }
  }

  formatMoney(cents) {
    // Basic money formatting - adjust based on your Shopify theme settings
    const amount = (cents / 100).toFixed(2);
    // Get currency symbol from Shopify or use default
    const currencySymbol = window.Shopify?.currency?.active || '€';
    return `${amount}${currencySymbol}`;
  }

  // ========================================
  // Pack Dropdown Methods
  // ========================================

  initializePackDropdown() {
    const packRadios = this.packElement.querySelectorAll('[data-pack-radio]');
    const dropdowns = this.packElement.querySelectorAll('[data-pack-dropdown]');
    const submitPriceElement = this.submitElement?.querySelector('[data-submit-price]');

    // Store pack state
    this.packState = {
      type: 'simple',
      secondProductId: null,
      secondProductHandle: null
    };

    // Update price on initial load
    this.updateAllPrices();

    // Handle pack option selection (simple vs duo) via radio buttons
    packRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        const packType = radio.value;
        this.packState.type = packType;

        // Update all prices
        this.updateAllPrices();

        // Dispatch pack type change event
        document.dispatchEvent(
          new CustomEvent('pack:type-changed', {
            detail: { packType },
          })
        );
      });
    });

    // Handle dropdown functionality
    dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector('[data-dropdown-trigger]');
      const menu = dropdown.querySelector('[data-dropdown-menu]');
      const valueElement = dropdown.querySelector('[data-dropdown-value]');
      const items = dropdown.querySelectorAll('[data-dropdown-item]');

      if (!trigger || !menu) return;

      // Set first available item as active by default
      const firstItem = Array.from(items).find((i) => i.dataset.unavailable !== 'true');
      if (firstItem) {
        firstItem.classList.add('product-form-pack-dropdown__item--active');
        dropdown.dataset.selectedProductId = firstItem.dataset.productId;
        dropdown.dataset.selectedProductHandle = firstItem.dataset.productHandle;

        if (this.packState) {
          this.packState.secondProductId = firstItem.dataset.productId;
          this.packState.secondProductHandle = firstItem.dataset.productHandle;
        }
      }

      // Prevent label click when interacting with dropdown
      dropdown.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });

      // Toggle dropdown on trigger click
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleDropdown(trigger);
      });

      // Handle item selection
      items.forEach((item) => {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (item.dataset.unavailable === 'true') return;

          const flavorName = item.dataset.flavorName;
          const productId = item.dataset.productId;
          const productHandle = item.dataset.productHandle;

          // Remove active class from all items
          items.forEach((i) =>
            i.classList.remove('product-form-pack-dropdown__item--active')
          );

          // Add active class to clicked item
          item.classList.add('product-form-pack-dropdown__item--active');

          // Update displayed value
          if (valueElement && flavorName) {
            valueElement.textContent = flavorName;
          }

          // Store selected product info
          dropdown.dataset.selectedProductId = productId;
          dropdown.dataset.selectedProductHandle = productHandle;

          // Update pack state with second product info
          if (this.packState) {
            this.packState.secondProductId = productId;
            this.packState.secondProductHandle = productHandle;
          }

          // Close dropdown
          this.closeDropdown(trigger);

          // Dispatch custom event for potential integration
          document.dispatchEvent(
            new CustomEvent('pack:flavor-selected', {
              detail: {
                flavorName,
                productId,
                productHandle,
              },
            })
          );
        });
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!dropdown.contains(e.target)) {
          this.closeDropdown(trigger);
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeDropdown(trigger);
        }
      });
    });
  }

  toggleDropdown(trigger) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.closeDropdown(trigger);
    } else {
      this.openDropdown(trigger);
    }
  }

  openDropdown(trigger) {
    trigger.setAttribute('aria-expanded', 'true');
    // The CSS handles the animation via the aria-expanded attribute
  }

  closeDropdown(trigger) {
    trigger.setAttribute('aria-expanded', 'false');
  }

  // ========================================
  // Subscription Methods
  // ========================================

  initializeSubscription() {
    const subscriptionRadios = this.subscriptionElement.querySelectorAll('[data-subscription-radio]');
    const frequencyRadios = this.subscriptionElement.querySelectorAll('[data-frequency-radio]');

    // Handle subscription type selection (subscription vs one-time)
    subscriptionRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        const purchaseType = radio.value;

        // Toggle a flag on pack/quantity blocks so they can hide their stacked discount
        this.togglePackQuantityDiscountUI(purchaseType === 'subscription');

        // Update price display
        this.updateAllPrices();

        // Dispatch subscription type change event
        document.dispatchEvent(
          new CustomEvent('subscription:type-changed', {
            detail: { purchaseType },
          })
        );
      });
    });

    // Handle frequency selection
    frequencyRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        const frequency = radio.value;

        // Update price display
        this.updateAllPrices();

        // Dispatch frequency change event
        document.dispatchEvent(
          new CustomEvent('subscription:frequency-changed', {
            detail: { frequency },
          })
        );
      });
    });

    // Update price on initial load
    const initialRadio = this.subscriptionElement.querySelector('[data-subscription-radio]:checked');
    if (initialRadio) {
      this.togglePackQuantityDiscountUI(initialRadio.value === 'subscription');
    }
    setTimeout(() => { this.updateAllPrices(); }, 0);
  }

  togglePackQuantityDiscountUI(subscriptionActive) {
    const swap = (radio, priceSelector) => {
      const label = radio.id ? this.element.querySelector(`label[for="${radio.id}"]`) : radio.nextElementSibling;
      if (!label) return;
      const priceEl = label.querySelector(priceSelector);
      if (!priceEl) return;
      if (!priceEl.dataset.originalText) priceEl.dataset.originalText = priceEl.textContent.trim();
      if (subscriptionActive && radio.dataset.priceOld) {
        priceEl.textContent = this.formatMoney(parseInt(radio.dataset.priceOld));
      } else {
        priceEl.textContent = priceEl.dataset.originalText;
      }
    };

    if (this.packElement) {
      this.packElement.classList.toggle('product-form-pack--subscription', subscriptionActive);
      this.packElement.querySelectorAll('[data-pack-radio]').forEach((radio) => {
        swap(radio, '.product-form-pack__option-price');
      });
    }
    if (this.quantityElement) {
      this.quantityElement.classList.toggle('product-form-quantity--subscription', subscriptionActive);
      this.quantityElement.querySelectorAll('[data-quantity-radio]').forEach((radio) => {
        swap(radio, '.product-form-quantity__option-price');
      });
    }
  }

  // ========================================
  // Videos Methods
  // ========================================

  initializeVideos() {
    const videoSliderElement = this.videosElement.querySelector('[data-video-slider]');

    if (!videoSliderElement) return;

    // Initialize video slider
    this.videoSlider = new Swiper(videoSliderElement, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: {
        enabled: true,
        sticky: false,
      },
      grabCursor: true,
      mousewheel: {
        forceToAxis: true,
      },
      breakpoints: {
        0: {
          slidesPerView: 'auto',
          spaceBetween: 8,
        },
        750: {
          slidesPerView: 'auto',
          spaceBetween: 8,
        },
      },
    });

    // Create modal overlay
    this.createVideoModal();

    // Get all videos
    const videos = this.videosElement.querySelectorAll('video');

    // Handle video play buttons
    const playButtons = this.videosElement.querySelectorAll('[data-video-play]');
    playButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const videoWrapper = button.closest('.product-form-videos__video-wrapper');
        const video = videoWrapper.querySelector('video');

        if (video) {
          this.openVideoModal(video);
        }
      });
    });

    // Handle click on video to open modal
    videos.forEach((video) => {
      video.addEventListener('click', (e) => {
        e.preventDefault();
        this.openVideoModal(video);
      });
    });
  }

  createVideoModal() {
    // Check if modal already exists
    if (document.querySelector('.product-form-videos__modal-overlay')) return;

    // Create modal overlay
    const modalOverlay = document.createElement('div');
    modalOverlay.className = 'product-form-videos__modal-overlay';
    modalOverlay.setAttribute('data-video-modal', '');

    // Create modal video container
    const modalVideo = document.createElement('div');
    modalVideo.className = 'product-form-videos__modal-video';

    // Create close button
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'product-form-videos__modal-close';
    closeButton.setAttribute('data-modal-close', '');
    closeButton.setAttribute('aria-label', 'Close video');
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    `;

    // Append close button to modal video
    modalVideo.appendChild(closeButton);

    // Append modal video to overlay
    modalOverlay.appendChild(modalVideo);

    // Append to body
    document.body.appendChild(modalOverlay);

    // Handle close button click
    closeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closeVideoModal();
    });

    // Handle click on overlay (outside video) to close
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) {
        this.closeVideoModal();
      }
    });

    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modalOverlay.classList.contains('is-active')) {
        this.closeVideoModal();
      }
    });
  }

  openVideoModal(video) {
    const modalOverlay = document.querySelector('.product-form-videos__modal-overlay');
    const modalVideo = modalOverlay.querySelector('.product-form-videos__modal-video');

    if (!modalOverlay || !modalVideo) return;

    // Clone the video element
    const videoClone = video.cloneNode(true);
    videoClone.removeAttribute('class');
    videoClone.muted = false;
    videoClone.controls = true;

    // Remove existing video if any
    const existingVideo = modalVideo.querySelector('video');
    if (existingVideo) {
      existingVideo.remove();
    }

    // Insert video before close button
    const closeButton = modalVideo.querySelector('.product-form-videos__modal-close');
    modalVideo.insertBefore(videoClone, closeButton);

    // Show modal
    modalOverlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';

    // Play video
    videoClone.play();

    // Auto-close on video end
    videoClone.addEventListener('ended', () => {
      this.closeVideoModal();
    });
  }

  closeVideoModal() {
    const modalOverlay = document.querySelector('.product-form-videos__modal-overlay');
    const modalVideo = modalOverlay?.querySelector('.product-form-videos__modal-video');

    if (!modalOverlay || !modalVideo) return;

    // Pause and remove video
    const video = modalVideo.querySelector('video');
    if (video) {
      video.pause();
      video.remove();
    }

    // Hide modal
    modalOverlay.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  // ========================================
  // Submit Form Methods
  // ========================================

  initializeSubmit() {
    const deliveryDateElement = this.submitElement.querySelector('[data-delivery-date]');
    const shippingAmountElement = this.submitElement.querySelector('[data-shipping-amount]');
    const submitPriceElement = this.submitElement.querySelector('[data-submit-price]');
    const freeShippingThreshold = parseInt(this.submitElement.dataset.freeShippingThreshold);
    const deliveryDaysEstimate = parseInt(this.submitElement.dataset.deliveryDaysEstimate || 3);

    // Calculate and display delivery date
    if (deliveryDateElement) {
      this.updateDeliveryDate(deliveryDateElement, deliveryDaysEstimate);
    }

    // Calculate and display free shipping remaining amount
    if (shippingAmountElement && submitPriceElement) {
      this.updateFreeShippingAmount(shippingAmountElement, submitPriceElement, freeShippingThreshold);
    }

    // Listen to variant changes to update price and free shipping
    document.addEventListener('variant:changed', (event) => {
      const variant = event.detail?.variant;
      if (submitPriceElement && variant?.price) {
        // Update base prices for updateAllPrices calculations
        this.baseProductPrice = variant.price;
        this.baseCompareAtPrice = variant.compare_at_price || null;
        submitPriceElement.dataset.compareAtPrice = variant.compare_at_price || '';

        // Re-run full price calculation (handles packs, quantities, subscriptions)
        this.updateAllPrices();
      }
      if (shippingAmountElement && submitPriceElement) {
        this.updateFreeShippingAmount(shippingAmountElement, submitPriceElement, freeShippingThreshold);
      }
    });

    // Listen to cart changes to update free shipping amount
    document.addEventListener('cart:updated', () => {
      if (shippingAmountElement && submitPriceElement) {
        this.updateFreeShippingAmount(shippingAmountElement, submitPriceElement, freeShippingThreshold);
      }
    });
  }

  updateDeliveryDate(element, daysToAdd) {
    const date = this.addBusinessDays(new Date(), daysToAdd);
    const locale = document.documentElement.lang || 'fr-FR';
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    element.textContent = date.toLocaleDateString(locale, options);
  }

  addBusinessDays(start, days) {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6 && !this.isFrenchHoliday(date)) {
        added++;
      }
    }
    return date;
  }

  isFrenchHoliday(date) {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const fixed = [
      [1, 1], [5, 1], [5, 8], [7, 14], [8, 15], [11, 1], [11, 11], [12, 25],
    ];
    if (fixed.some(([fm, fd]) => fm === m && fd === d)) return true;
    const easter = this.computeEaster(y);
    const easterMonday = new Date(easter); easterMonday.setDate(easter.getDate() + 1);
    const ascension = new Date(easter); ascension.setDate(easter.getDate() + 39);
    const pentecost = new Date(easter); pentecost.setDate(easter.getDate() + 50);
    return [easterMonday, ascension, pentecost].some(
      (h) => h.getMonth() === date.getMonth() && h.getDate() === date.getDate()
    );
  }

  computeEaster(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }

  async updateFreeShippingAmount(element, priceElement, threshold) {
    try {
      // Get cart total
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartTotal = cart.total_price; // in cents

      // Get current product price from text content
      const priceText = priceElement.textContent;
      const priceMatch = priceText.match(/[\d,]+/);
      let productPrice = 0;

      if (priceMatch) {
        // Convert price string to cents (e.g., "31,90€" => 3190)
        productPrice = Math.round(parseFloat(priceMatch[0].replace(',', '.')) * 100);
      }

      // Calculate total (cart + current product)
      const total = cartTotal + productPrice;

      // Calculate remaining amount
      const remaining = threshold - total;

      if (remaining > 0) {
        // Convert cents to euros and format
        const remainingEuros = (remaining / 100).toFixed(2).replace('.', ',');
        element.textContent = `${remainingEuros}€`;
        element.closest('[data-shipping-remaining]').style.display = '';
      } else {
        // Hide the remaining text if threshold is reached
        element.closest('[data-shipping-remaining]').style.display = 'none';
      }
    } catch (error) {
      console.error('Error updating free shipping amount:', error);
    }
  }

  formatMoney(cents) {
    const euros = cents / 100;
    return `${euros.toFixed(2).replace('.', ',')}€`;
  }

  // ========================================
  // Price Calculation Methods
  // ========================================

  updateAllPrices() {
    const submitPriceElement = this.submitElement?.querySelector('[data-submit-price]');
    if (!submitPriceElement) return;

    // Get base price
    let basePrice = parseInt(this.baseProductPrice);
    if (basePrice === null || isNaN(basePrice)) return;

    // Get quantity
    let quantity = 1;
    if (this.quantityElement && this.selectedQuantity) {
      quantity = this.selectedQuantity;
    } else if (this.packElement && this.packState) {
      quantity = this.packState.type === 'duo' ? 2 : 1;
    }

    // Determine if subscription is active — overrides pack/quantity discount (no stacking)
    let subscriptionActive = false;
    let subscriptionFrequencyRadio = null;
    if (this.subscriptionElement) {
      const subscriptionRadio = this.subscriptionElement.querySelector('[data-subscription-radio]:checked');
      if (subscriptionRadio && subscriptionRadio.value === 'subscription') {
        subscriptionActive = true;
        subscriptionFrequencyRadio = this.subscriptionElement.querySelector('[data-frequency-radio]:checked');
      }
    }

    // Calculate base total
    let totalPrice = basePrice * quantity;
    let priceBeforeDiscount = totalPrice;
    let quantityOrPackDiscount = 0;

    // Apply quantity/pack discount only if subscription is NOT selected
    if (!subscriptionActive) {
      if (this.quantityElement && this.selectedQuantity) {
        const quantityRadio = this.quantityElement.querySelector('[data-quantity-radio]:checked');
        if (quantityRadio) {
          const discountedPrice = parseInt(quantityRadio.dataset.price);
          const regularPrice = parseInt(quantityRadio.dataset.priceOld);
          if (discountedPrice && regularPrice) {
            quantityOrPackDiscount = regularPrice - discountedPrice;
            totalPrice = discountedPrice;
            priceBeforeDiscount = regularPrice;
          }
        }
      } else if (this.packElement && this.packState && this.packState.type === 'duo') {
        const packRadio = this.packElement.querySelector('[data-pack-radio="duo"]');
        if (packRadio) {
          const discountedPrice = parseInt(packRadio.dataset.price);
          const regularPrice = parseInt(packRadio.dataset.priceOld);
          if (discountedPrice && regularPrice) {
            quantityOrPackDiscount = regularPrice - discountedPrice;
            totalPrice = discountedPrice;
            priceBeforeDiscount = regularPrice;
          }
        }
      }
    }

    // Apply subscription discount on top of base price (replaces pack/quantity discount)
    let subscriptionDiscount = 0;
    if (subscriptionActive && subscriptionFrequencyRadio) {
      const adjustmentValue = parseFloat(subscriptionFrequencyRadio.dataset.priceAdjustmentValue);
      const adjustmentType = subscriptionFrequencyRadio.dataset.priceAdjustmentType;

      if (adjustmentValue && adjustmentType === 'percentage') {
        subscriptionDiscount = Math.round(totalPrice * adjustmentValue / 100);
        totalPrice = totalPrice - subscriptionDiscount;
      } else if (adjustmentValue && adjustmentType === 'fixed_amount') {
        subscriptionDiscount = adjustmentValue * quantity;
        totalPrice = totalPrice - subscriptionDiscount;
      } else if (adjustmentValue && adjustmentType === 'price') {
        totalPrice = adjustmentValue * quantity;
        subscriptionDiscount = priceBeforeDiscount - totalPrice;
      }
    }

    // Format and display price
    const formattedPrice = this.formatMoney(totalPrice);

    // Determine the strikethrough price
    // Priority: pack/quantity/subscription discounts first, then compare_at_price
    const totalDiscount = quantityOrPackDiscount + subscriptionDiscount;
    let strikeThroughPrice = null;

    if (totalDiscount > 0 && priceBeforeDiscount > totalPrice) {
      strikeThroughPrice = priceBeforeDiscount;
    } else if (this.baseCompareAtPrice && this.baseCompareAtPrice > totalPrice) {
      // Use compare_at_price scaled by quantity
      strikeThroughPrice = this.baseCompareAtPrice * quantity;
    }

    if (strikeThroughPrice && strikeThroughPrice > totalPrice) {
      const formattedPriceOld = this.formatMoney(strikeThroughPrice);
      submitPriceElement.innerHTML = `${formattedPrice}<span class="product-form-submit__button-price-compare" data-submit-compare-price>${formattedPriceOld}</span>`;
    } else {
      submitPriceElement.textContent = formattedPrice;
    }

    // Sync sticky bar price
    this.syncStickyBarPrice();
  }

  // ========================================
  // Quantity Selector Methods
  // ========================================

  initializeQuantitySelector() {
    const quantityRadios = this.quantityElement.querySelectorAll('[data-quantity-radio]');
    const submitPriceElement = this.submitElement?.querySelector('[data-submit-price]');

    if (!quantityRadios.length) return;

    // Store current quantity in a data attribute or property
    this.selectedQuantity = 1;

    // Get the initially selected quantity
    const initiallyChecked = this.quantityElement.querySelector('[data-quantity-radio]:checked');
    if (initiallyChecked) {
      this.selectedQuantity = parseInt(initiallyChecked.dataset.quantity);
      const initialPrice = parseInt(initiallyChecked.dataset.price);
      if (submitPriceElement && initialPrice) {
        submitPriceElement.textContent = this.formatMoney(initialPrice);
      }
    }

    // Listen to quantity changes
    quantityRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          const quantity = parseInt(radio.dataset.quantity);
          const price = parseInt(radio.dataset.price);

          // Update selected quantity
          this.selectedQuantity = quantity;

          // Update all prices
          this.updateAllPrices();

          // Update free shipping amount if it exists
          const shippingAmountElement = this.submitElement?.querySelector('[data-shipping-amount]');
          const freeShippingThreshold = parseInt(this.submitElement?.dataset.freeShippingThreshold || 0);
          if (shippingAmountElement && submitPriceElement && freeShippingThreshold) {
            this.updateFreeShippingAmount(shippingAmountElement, submitPriceElement, freeShippingThreshold);
          }

          // Dispatch custom event for quantity change
          document.dispatchEvent(new CustomEvent('quantity:changed', {
            detail: {
              quantity: quantity,
              price: price
            }
          }));
        }
      });
    });

  }

  // ========================================
  // Form Submit Methods
  // ========================================

  initializeFormSubmit() {
    const form = this.element.closest('form') || this.element.querySelector('form');
    if (!form) return;

    // Prevent default form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Hide any previous errors
      this.hideError();

      // Get submit button
      const submitButton = this.submitElement?.querySelector('[data-submit-button]');

      // Add loading state
      if (submitButton) {
        submitButton.classList.add('loading');
        submitButton.disabled = true;
      }

      try {
        // Get selling plan if subscription is selected
        let sellingPlan = null;
        if (this.subscriptionElement) {
          const subscriptionRadio = this.subscriptionElement.querySelector('[data-subscription-radio]:checked');
          console.log('Subscription radio:', subscriptionRadio);
          if (subscriptionRadio && subscriptionRadio.value === 'subscription') {
            const frequencyRadio = this.subscriptionElement.querySelector('[data-frequency-radio]:checked');
            console.log('Frequency radio:', frequencyRadio);
            if (frequencyRadio) {
              sellingPlan = frequencyRadio.dataset.sellingPlanId;
              console.log('Selling plan ID:', sellingPlan);
            }
          }
        }

        // Handle pack duo: add 2 products separately
        if (this.packElement && this.packState && this.packState.type === 'duo') {
          await this.handlePackDuoSubmit(form, sellingPlan);
        }
        // Handle quantity selector
        else if (this.quantityElement && this.selectedQuantity) {
          await this.handleQuantitySubmit(form, sellingPlan);
        }
        // Normal form submission
        else {
          await this.handleNormalSubmit(form, sellingPlan);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        this.showError(error.message || 'Erreur lors de l\'ajout au panier');
      } finally {
        // Remove loading state
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.disabled = false;
        }
      }
    });
  }

  async handlePackDuoSubmit(form, sellingPlan) {
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const secondProductId = this.packState.secondProductId;
    const secondProductHandle = this.packState.secondProductHandle;

    if (!variantId || !secondProductId) {
      console.error('Missing product IDs for pack duo');
      throw new Error('ID de produit manquant pour le pack duo');
    }

    console.log('Pack duo submit - First product:', variantId, 'Second product:', secondProductId, 'Selling plan:', sellingPlan);

    // Get second product selling plan if subscription is selected
    let secondProductSellingPlan = null;
    if (sellingPlan && secondProductHandle) {
      try {
        // Get selling plan name from the first product
        const sellingPlanName = this.getSelectedSellingPlanName();
        console.log('Selected selling plan name:', sellingPlanName);

        // Fetch second product data to get its selling plans
        secondProductSellingPlan = await this.getProductSellingPlan(secondProductHandle, sellingPlanName);
        console.log('Second product selling plan ID:', secondProductSellingPlan);

        if (!secondProductSellingPlan) {
          throw new Error(`Le produit sélectionné n'est pas disponible en abonnement avec la fréquence "${sellingPlanName}"`);
        }
      } catch (error) {
        console.error('Error getting second product selling plan:', error);
        throw new Error(error.message || 'Impossible de récupérer l\'abonnement pour le 2ème produit');
      }
    }

    // Prepare items array
    const items = [
      {
        id: variantId,
        quantity: 1,
        selling_plan: sellingPlan || undefined
      },
      {
        id: secondProductId,
        quantity: 1,
        selling_plan: secondProductSellingPlan || undefined
      }
    ];

    // Add to cart
    await this.addItemsToCart(items);
  }

  async handleQuantitySubmit(form, sellingPlan) {
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = this.selectedQuantity || 1;

    if (!variantId) {
      console.error('Missing product ID');
      throw new Error('ID de produit manquant');
    }

    const items = [
      {
        id: variantId,
        quantity: quantity,
        selling_plan: sellingPlan || undefined
      }
    ];

    await this.addItemsToCart(items);
  }

  async handleNormalSubmit(form, sellingPlan) {
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = formData.get('quantity') || 1;

    console.log('Normal submit - Variant ID:', variantId, 'Selling Plan:', sellingPlan);

    if (!variantId) {
      console.error('Missing product ID');
      throw new Error('ID de produit manquant');
    }

    const items = [
      {
        id: variantId,
        quantity: parseInt(quantity),
        selling_plan: sellingPlan || undefined
      }
    ];

    await this.addItemsToCart(items);
  }

  async addItemsToCart(items) {
    try {
      // Prepare items payload
      const payload = {
        items: items.map(item => {
          const itemData = {
            id: parseInt(item.id),
            quantity: item.quantity
          };

          // Only add selling_plan if it exists and is valid
          if (item.selling_plan && item.selling_plan !== 'undefined' && item.selling_plan !== '') {
            itemData.selling_plan = parseInt(item.selling_plan);
          }

          return itemData;
        })
      };

      console.log('Adding to cart:', payload);

      // Add items to cart using Shopify Cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Cart add error response:', errorText);
        let error;
        try {
          error = JSON.parse(errorText);
        } catch (e) {
          error = { description: errorText };
        }
        console.error('Cart add error:', error);
        throw new Error(error.description || error.message || 'Failed to add products to cart');
      }

      const result = await response.json();
      console.log('Cart add success:', result);

      // Fetch updated cart data
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      console.log('Updated cart data:', cart);

      // Dispatch cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

      // Get cart drawer element
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer) {
        console.log('Cart drawer found, refreshing and opening...');

        // Fetch rendered sections for cart drawer using section rendering
        try {
          const sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];
          const sectionsResponse = await fetch(`${window.Shopify.routes.root}?sections=${sectionsToRender.join(',')}`);

          if (!sectionsResponse.ok) {
            throw new Error('Failed to fetch sections');
          }

          const sectionsData = await sectionsResponse.json();
          console.log('Sections data received:', sectionsData);

          // Build the data structure that renderContents expects
          const parsedState = {
            sections: sectionsData
          };

          console.log('Parsed state for cart drawer:', parsedState);

          // Remove is-empty class if cart was empty before
          if (cartDrawer.classList.contains('is-empty')) {
            cartDrawer.classList.remove('is-empty');
          }

          // Refresh cart contents using renderContents
          if (typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(parsedState);
          } else {
            // Fallback: manually update the cart drawer content
            const cartDrawerItems = document.querySelector('cart-drawer-items');
            const cartDrawerFooter = document.querySelector('.cart-drawer__footer');

            if (cartDrawerItems && sectionsData['cart-drawer']) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionsData['cart-drawer'], 'text/html');

              const newCartDrawerItems = doc.querySelector('cart-drawer-items');
              const newCartDrawerFooter = doc.querySelector('.cart-drawer__footer');

              if (newCartDrawerItems) {
                cartDrawerItems.replaceWith(newCartDrawerItems);
              }
              if (newCartDrawerFooter && cartDrawerFooter) {
                cartDrawerFooter.replaceWith(newCartDrawerFooter);
              }
            }

            // Open the drawer
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              cartDrawer.classList.add('animate', 'active');
            }
          }

          // Update cart icon if needed
          if (sectionsData['cart-icon-bubble']) {
            const cartIconBubble = document.getElementById('cart-icon-bubble');
            if (cartIconBubble) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionsData['cart-icon-bubble'], 'text/html');
              const newCartIcon = doc.querySelector('.shopify-section');
              if (newCartIcon) {
                cartIconBubble.innerHTML = newCartIcon.innerHTML;
              }
            }
          }
        } catch (error) {
          console.error('Error fetching sections for cart drawer:', error);

          // Fallback: Force refresh the entire cart drawer by fetching the cart-drawer section
          try {
            const fallbackResponse = await fetch(`/cart?section_id=cart-drawer`);
            const fallbackHtml = await fallbackResponse.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(fallbackHtml, 'text/html');

            const newDrawerInner = doc.querySelector('.drawer__inner');
            const currentDrawerInner = cartDrawer.querySelector('.drawer__inner');

            if (newDrawerInner && currentDrawerInner) {
              currentDrawerInner.replaceWith(newDrawerInner);

              // Remove is-empty class
              if (cartDrawer.classList.contains('is-empty')) {
                cartDrawer.classList.remove('is-empty');
              }
            }

            // Open the drawer
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              setTimeout(() => {
                cartDrawer.classList.add('animate', 'active');
              }, 100);
            }
          } catch (fallbackError) {
            console.error('Fallback cart refresh also failed:', fallbackError);
            // Last resort: just open the drawer with current content
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              cartDrawer.classList.add('animate', 'active');
            }
          }
        }

      } else {
        // Fallback: Click on cart icon to open drawer
        console.log('Cart drawer not found, trying cart icon click...');

        const cartIcon = document.querySelector('[data-cart-icon]') ||
          document.querySelector('#cart-icon-bubble') ||
          document.querySelector('.header__icon--cart') ||
          document.querySelector('a[href="/cart"]');

        if (cartIcon) {
          cartIcon.click();
        }
      }

      return result;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  // ========================================
  // Selling Plan Helper Methods
  // ========================================

  getSelectedSellingPlanName() {
    if (!this.subscriptionElement) return null;

    const frequencyRadio = this.subscriptionElement.querySelector('[data-frequency-radio]:checked');
    if (!frequencyRadio) return null;

    return frequencyRadio.dataset.sellingPlanName || null;
  }

  async getProductSellingPlan(productHandle, sellingPlanName) {
    if (!productHandle || !sellingPlanName) return null;

    try {
      // Fetch product data with selling plans
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        throw new Error('Failed to fetch product data');
      }

      const productData = await response.json();
      console.log('Second product data:', productData);

      // Check if product has selling plan groups
      if (!productData.selling_plan_groups || productData.selling_plan_groups.length === 0) {
        console.error('Second product has no selling plans');
        return null;
      }

      // Find the matching selling plan by name
      for (const group of productData.selling_plan_groups) {
        for (const plan of group.selling_plans) {
          if (plan.name === sellingPlanName) {
            console.log('Found matching selling plan:', plan);
            return plan.id.toString();
          }
        }
      }

      console.error('No matching selling plan found for:', sellingPlanName);
      return null;
    } catch (error) {
      console.error('Error fetching product selling plans:', error);
      return null;
    }
  }

  // ========================================
  // Error Display Methods
  // ========================================

  showError(message) {
    const errorElement = this.submitElement?.querySelector('[data-submit-error]');
    const errorMessage = this.submitElement?.querySelector('.product-form-submit__error-message');

    if (errorElement && errorMessage) {
      errorMessage.textContent = message;
      errorElement.style.display = 'block';

      // Auto-hide after 5 seconds
      setTimeout(() => {
        this.hideError();
      }, 5000);
    }
  }

  hideError() {
    const errorElement = this.submitElement?.querySelector('[data-submit-error]');
    if (errorElement) {
      errorElement.style.display = 'none';
    }
  }

  // ========================================
  // Sticky Bar Methods
  // ========================================

  initializeStickyBar() {
    const stickyBar = this.stickyBarElement;
    const toggleButton = stickyBar.querySelector('[data-sticky-toggle]');
    const expandedContent = stickyBar.querySelector('[data-sticky-expanded]');
    const overlay = document.querySelector('.product-sticky-bar--overlay');
    const formClone = stickyBar.querySelector('[data-sticky-form-clone]');
    const subscriptionClone = stickyBar.querySelector('[data-sticky-subscription-clone]');
    const addToCartButton = stickyBar.querySelector('[data-sticky-add-to-cart]');
    const stickyPrice = stickyBar.querySelector('[data-sticky-price]');
    const deliverySlider = stickyBar.querySelector('[data-sticky-delivery-slider]');
    const deliveryDateElement = stickyBar.querySelector('[data-sticky-delivery-date]');
    const lowStockElement = stickyBar.querySelector('[data-sticky-low-stock]');

    // Initialize delivery date if exists
    if (deliveryDateElement && this.submitElement) {
      const deliveryDays = parseInt(this.submitElement.dataset.deliveryDaysEstimate || 3);
      this.updateDeliveryDate(deliveryDateElement, deliveryDays);
    }

    // Initialize delivery slider if exists
    if (deliverySlider) {
      this.initializeStickyDeliverySlider(deliverySlider);
    }

    // Clone pack/quantity forms if exists
    if (formClone) {
      this.cloneAndSyncFormToStickyBar(formClone);
    }

    // Clone subscription form if exists
    if (subscriptionClone) {
      this.cloneAndSyncSubscriptionToStickyBar(subscriptionClone);
    }

    // Initialize low stock indicator if exists
    if (lowStockElement) {
      this.initializeStickyLowStock();
    }

    // Handle toggle button
    if (toggleButton && expandedContent && overlay) {
      toggleButton.addEventListener('click', () => {
        const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          toggleButton.setAttribute('aria-expanded', 'false');
          expandedContent.classList.remove('is-open');
          overlay.classList.remove('is-open');
        } else {
          toggleButton.setAttribute('aria-expanded', 'true');
          expandedContent.classList.add('is-open');
          overlay.classList.add('is-open');
        }
      });

      // Close expanded when clicking on overlay
      overlay.addEventListener('click', () => {
        toggleButton.setAttribute('aria-expanded', 'false');
        expandedContent.classList.remove('is-open');
        overlay.classList.remove('is-open');
      });

      // Prevent clicks inside expanded container from bubbling to overlay
      const container = expandedContent.querySelector('.product-sticky-bar__expanded-container');
      if (container) {
        container.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      }
    }

    // Sync price with main form
    this.syncStickyBarPrice();

    // Listen to price changes
    document.addEventListener('quantity:changed', () => {
      this.syncStickyBarPrice();
    });

    document.addEventListener('pack:type-changed', () => {
      this.syncStickyBarPrice();
    });

    document.addEventListener('subscription:type-changed', () => {
      this.syncStickyBarPrice();
    });

    // Handle add to cart button
    if (addToCartButton) {
      addToCartButton.addEventListener('click', () => {
        // Trigger the main form submit
        const mainForm = this.element.closest('form') || this.element.querySelector('form');
        if (mainForm) {
          mainForm.requestSubmit();
        }
      });
    }

    // Handle scroll to show/hide sticky bar
    this.initializeStickyBarScroll();

    // Update sticky bar height CSS variable
    this.updateStickyBarHeight();

    // Update height on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateStickyBarHeight();
      }, 100);
    });
  }

  initializeStickyBarScroll() {
    const stickyBar = this.stickyBarElement;
    const mainSubmitButton = this.submitElement?.querySelector('[data-submit-button]');
    const expandedContent = stickyBar.querySelector('[data-sticky-expanded]');
    const toggleButton = stickyBar.querySelector('[data-sticky-toggle]');
    const overlay = document.querySelector('.product-sticky-bar--overlay');

    if (!mainSubmitButton) return;

    // Get the observer element (the main submit button)
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        // Show sticky bar when main button is not visible
        if (!entry.isIntersecting) {
          stickyBar.setAttribute('data-sticky-hidden', 'false');
        } else {
          stickyBar.setAttribute('data-sticky-hidden', 'true');
          // Close expanded content when sticky bar hides
          if (expandedContent && toggleButton && overlay) {
            expandedContent.classList.remove('is-open');
            overlay.classList.remove('is-open');
            toggleButton.setAttribute('aria-expanded', 'false');
          }
        }
      });
    };

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    observer.observe(mainSubmitButton);

    // Store observer for cleanup
    this.stickyBarObserver = observer;
  }

  cloneAndSyncFormToStickyBar(formClone) {
    // Find pack and quantity elements in the main form
    const packElement = this.element.querySelector('[data-product-pack]');
    const quantityElement = this.element.querySelector('[data-product-quantity]');

    if (packElement) {
      const packClone = packElement.cloneNode(true);

      // Make IDs and FOR attributes unique by adding -sticky suffix
      packClone.querySelectorAll('input[id]').forEach(input => {
        const originalId = input.id;
        input.id = originalId + '-sticky';
      });

      packClone.querySelectorAll('label[for]').forEach(label => {
        const originalFor = label.getAttribute('for');
        label.setAttribute('for', originalFor + '-sticky');
      });

      formClone.appendChild(packClone);

      // Setup bidirectional sync for pack
      this.setupPackBidirectionalSync(packElement, packClone);
    }

    if (quantityElement) {
      const quantityClone = quantityElement.cloneNode(true);

      // Make IDs and FOR attributes unique by adding -sticky suffix
      quantityClone.querySelectorAll('input[id]').forEach(input => {
        const originalId = input.id;
        input.id = originalId + '-sticky';
      });

      quantityClone.querySelectorAll('label[for]').forEach(label => {
        const originalFor = label.getAttribute('for');
        label.setAttribute('for', originalFor + '-sticky');
      });

      formClone.appendChild(quantityClone);

      // Setup bidirectional sync for quantity
      this.setupQuantityBidirectionalSync(quantityElement, quantityClone);
    }
  }

  cloneAndSyncSubscriptionToStickyBar(subscriptionClone) {
    // Find subscription element in the main form
    const subscriptionElement = this.element.querySelector('[data-product-subscription]');

    if (subscriptionElement) {
      const subscriptionClonedElement = subscriptionElement.cloneNode(true);

      // Make IDs and FOR attributes unique by adding -sticky suffix
      subscriptionClonedElement.querySelectorAll('input[id]').forEach(input => {
        const originalId = input.id;
        input.id = originalId + '-sticky';
      });

      subscriptionClonedElement.querySelectorAll('label[for]').forEach(label => {
        const originalFor = label.getAttribute('for');
        label.setAttribute('for', originalFor + '-sticky');
      });

      subscriptionClone.appendChild(subscriptionClonedElement);

      // Setup bidirectional sync for subscription
      this.setupSubscriptionBidirectionalSync(subscriptionElement, subscriptionClonedElement);
    }
  }

  setupPackBidirectionalSync(originalPack, clonedPack) {
    // Sync radios (pack type selection)
    const originalRadios = originalPack.querySelectorAll('[data-pack-radio]');
    const clonedRadios = clonedPack.querySelectorAll('[data-pack-radio]');

    // Original → Clone
    originalRadios.forEach((radio, index) => {
      radio.addEventListener('change', () => {
        if (radio.checked && clonedRadios[index]) {
          clonedRadios[index].checked = true;
        }
      });
    });

    // Clone → Original
    clonedRadios.forEach((radio, index) => {
      radio.addEventListener('change', (e) => {
        if (radio.checked && originalRadios[index]) {
          // Prevent default behavior that might cause scroll
          e.preventDefault();

          // Sync without scrolling
          originalRadios[index].checked = true;

          // Dispatch event without causing focus/scroll
          const changeEvent = new Event('change', { bubbles: true });
          originalRadios[index].dispatchEvent(changeEvent);
        }
      });
    });

    // Sync dropdown if exists
    const originalDropdown = originalPack.querySelector('[data-pack-dropdown]');
    const clonedDropdown = clonedPack.querySelector('[data-pack-dropdown]');

    if (originalDropdown && clonedDropdown) {
      this.setupDropdownBidirectionalSync(originalDropdown, clonedDropdown);
    }
  }

  setupQuantityBidirectionalSync(originalQuantity, clonedQuantity) {
    const originalRadios = originalQuantity.querySelectorAll('[data-quantity-radio]');
    const clonedRadios = clonedQuantity.querySelectorAll('[data-quantity-radio]');

    // Original → Clone
    originalRadios.forEach((radio, index) => {
      radio.addEventListener('change', () => {
        if (radio.checked && clonedRadios[index]) {
          clonedRadios[index].checked = true;
        }
      });
    });

    // Clone → Original
    clonedRadios.forEach((radio, index) => {
      radio.addEventListener('change', (e) => {
        if (radio.checked && originalRadios[index]) {
          // Prevent default behavior that might cause scroll
          e.preventDefault();

          // Sync without scrolling
          originalRadios[index].checked = true;

          // Dispatch event without causing focus/scroll
          const changeEvent = new Event('change', { bubbles: true });
          originalRadios[index].dispatchEvent(changeEvent);
        }
      });
    });
  }

  setupSubscriptionBidirectionalSync(originalSubscription, clonedSubscription) {
    // Sync subscription type radios
    const originalTypeRadios = originalSubscription.querySelectorAll('[data-subscription-radio]');
    const clonedTypeRadios = clonedSubscription.querySelectorAll('[data-subscription-radio]');

    // Original → Clone
    originalTypeRadios.forEach((radio, index) => {
      radio.addEventListener('change', () => {
        if (radio.checked && clonedTypeRadios[index]) {
          clonedTypeRadios[index].checked = true;
        }
      });
    });

    // Clone → Original (with business logic)
    clonedTypeRadios.forEach((radio, index) => {
      radio.addEventListener('change', (e) => {
        if (radio.checked && originalTypeRadios[index]) {
          // Prevent scroll
          e.preventDefault();

          // Sync to original
          originalTypeRadios[index].checked = true;

          // Trigger the original radio's change event to run business logic
          const changeEvent = new Event('change', { bubbles: true });
          originalTypeRadios[index].dispatchEvent(changeEvent);

          // Also trigger the business logic directly for the clone
          const purchaseType = radio.value;

          // Update price display
          this.updateAllPrices();

          // Dispatch subscription type change event
          document.dispatchEvent(
            new CustomEvent('subscription:type-changed', {
              detail: { purchaseType },
            })
          );
        }
      });
    });

    // Sync frequency radios
    const originalFrequencyRadios = originalSubscription.querySelectorAll('[data-frequency-radio]');
    const clonedFrequencyRadios = clonedSubscription.querySelectorAll('[data-frequency-radio]');

    // Original → Clone
    originalFrequencyRadios.forEach((radio, index) => {
      radio.addEventListener('change', () => {
        if (radio.checked && clonedFrequencyRadios[index]) {
          clonedFrequencyRadios[index].checked = true;
        }
      });
    });

    // Clone → Original (with business logic)
    clonedFrequencyRadios.forEach((radio, index) => {
      radio.addEventListener('change', (e) => {
        if (radio.checked && originalFrequencyRadios[index]) {
          // Prevent scroll
          e.preventDefault();

          // Sync to original
          originalFrequencyRadios[index].checked = true;

          // Trigger the original radio's change event to run business logic
          const changeEvent = new Event('change', { bubbles: true });
          originalFrequencyRadios[index].dispatchEvent(changeEvent);

          // Also trigger the business logic directly for the clone
          const frequency = radio.value;

          // Update price display
          this.updateAllPrices();

          // Dispatch frequency change event
          document.dispatchEvent(
            new CustomEvent('subscription:frequency-changed', {
              detail: { frequency },
            })
          );
        }
      });
    });
  }

  setupDropdownBidirectionalSync(originalDropdown, clonedDropdown) {
    const originalTrigger = originalDropdown.querySelector('[data-dropdown-trigger]');
    const clonedTrigger = clonedDropdown.querySelector('[data-dropdown-trigger]');
    const originalItems = originalDropdown.querySelectorAll('[data-dropdown-item]');
    const clonedItems = clonedDropdown.querySelectorAll('[data-dropdown-item]');
    const originalValue = originalDropdown.querySelector('[data-dropdown-value]');
    const clonedValue = clonedDropdown.querySelector('[data-dropdown-value]');

    // Setup cloned dropdown functionality
    if (clonedTrigger) {
      clonedTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleDropdown(clonedTrigger);
      });
    }

    // Clone → Original sync
    clonedItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const flavorName = item.dataset.flavorName;

        // Update cloned dropdown
        clonedItems.forEach((i) => i.classList.remove('product-form-pack-dropdown__item--active'));
        item.classList.add('product-form-pack-dropdown__item--active');
        if (clonedValue && flavorName) {
          clonedValue.textContent = flavorName;
        }
        this.closeDropdown(clonedTrigger);

        // Sync to original
        if (originalItems[index]) {
          originalItems.forEach((i) => i.classList.remove('product-form-pack-dropdown__item--active'));
          originalItems[index].classList.add('product-form-pack-dropdown__item--active');
          if (originalValue && flavorName) {
            originalValue.textContent = flavorName;
          }
          originalItems[index].click();
        }
      });
    });

    // Original → Clone sync
    originalItems.forEach((item, index) => {
      const originalClickHandler = () => {
        const flavorName = item.dataset.flavorName;

        // Update clone dropdown to match
        if (clonedItems[index] && clonedValue && flavorName) {
          clonedItems.forEach((i) => i.classList.remove('product-form-pack-dropdown__item--active'));
          clonedItems[index].classList.add('product-form-pack-dropdown__item--active');
          clonedValue.textContent = flavorName;
        }
      };

      item.addEventListener('click', originalClickHandler);
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!clonedDropdown.contains(e.target)) {
        this.closeDropdown(clonedTrigger);
      }
    });
  }

  syncStickyBarPrice() {
    if (!this.stickyBarElement) return;

    const stickyPrice = this.stickyBarElement.querySelector('[data-sticky-price]');
    const mainPrice = this.submitElement?.querySelector('[data-submit-price]');

    if (stickyPrice && mainPrice) {
      stickyPrice.innerHTML = mainPrice.innerHTML;
    }
  }

  initializeStickyDeliverySlider(sliderElement) {
    // Wait for Swiper to be loaded
    if (typeof Swiper === 'undefined') return;

    const swiperContainer = sliderElement.querySelector('.swiper');
    if (!swiperContainer) return;

    // Initialize slider with native pagination
    this.stickyDeliverySlider = new Swiper(swiperContainer, {
      direction: 'horizontal',
      slidesPerView: 1,
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      speed: 500,
      effect: 'slide',
      pagination: {
        el: '.product-sticky-bar__delivery .swiper-pagination',
        clickable: true,
        type: 'bullets',
      },
    });
  }

  updateStickyBarHeight() {
    if (!this.stickyBarElement) return;

    const height = this.stickyBarElement.offsetHeight;
    document.documentElement.style.setProperty('--sticky-bar-height', `${height}px`);
  }

  initializeStickyLowStock() {
    // This method handles the conditional display of low stock vs delivery slider
    const lowStockElement = this.stickyBarElement.querySelector('[data-sticky-low-stock]');
    const deliveryElement = this.stickyBarElement.querySelector('[data-sticky-delivery]');

    if (!lowStockElement || !deliveryElement) return;

    // Get threshold
    this.stickyStockThreshold = parseInt(lowStockElement.dataset.threshold) || 20;

    // Update display based on current variant
    this.updateStickyLowStockDisplay();

    // Listen for variant changes
    document.addEventListener('variant:changed', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateStickyLowStockDisplayFromVariant(event.detail.variant);
      }
    });
  }

  async updateStickyLowStockDisplay() {
    const form = this.element.querySelector('form[data-product-form]');
    if (!form) return;

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!variantIdInput) return;

    const variantId = variantIdInput.value;

    try {
      // Fetch variant data from Shopify API
      const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
      if (!productHandle) return;

      const response = await fetch(`/products/${productHandle}.js`);
      const productData = await response.json();

      // Find the variant
      const variant = productData.variants.find(v => v.id.toString() === variantId.toString());
      if (variant) {
        this.updateStickyLowStockDisplayFromVariant(variant);
      }
    } catch (error) {
      console.error('Error fetching variant data for sticky bar:', error);
    }
  }

  updateStickyLowStockDisplayFromVariant(variant) {
    const lowStockElement = this.stickyBarElement?.querySelector('[data-sticky-low-stock]');
    const deliveryElement = this.stickyBarElement?.querySelector('[data-sticky-delivery]');

    if (!lowStockElement || !deliveryElement) return;

    const inventory = variant.inventory_quantity;
    const available = variant.available;

    // Show low stock and hide delivery if stock <= threshold and stock > 0
    if (available && inventory > 0 && inventory <= this.stickyStockThreshold) {
      lowStockElement.style.display = 'flex';
      deliveryElement.style.display = 'none';

      // Update stock count
      const stockCountElement = lowStockElement.querySelector('[data-sticky-stock-count]');
      if (stockCountElement) {
        stockCountElement.textContent = inventory;
      }

      // Update bar width
      const stockBarElement = lowStockElement.querySelector('[data-sticky-stock-bar]');
      if (stockBarElement) {
        const percentage = Math.min(100, (inventory / this.stickyStockThreshold) * 100);
        stockBarElement.style.width = `${percentage}%`;
      }
    } else {
      // Hide low stock and show delivery
      lowStockElement.style.display = 'none';
      deliveryElement.style.display = 'flex';
    }
  }

  // ========================================
  // Low Stock Indicator Methods
  // ========================================

  initializeLowStockIndicator() {
    // Get threshold from data attribute
    this.stockThreshold = parseInt(this.lowStockElement.dataset.threshold) || 20;

    // Listen for form data change (variant change from Shopify theme)
    const form = this.element.querySelector('form[data-product-form]');
    if (form) {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput) {
        // Create a MutationObserver to watch for variant ID changes
        const observer = new MutationObserver(() => {
          this.updateLowStockIndicator();
        });

        observer.observe(variantIdInput, {
          attributes: true,
          attributeFilter: ['value']
        });

        this.lowStockObserver = observer;
      }
    }

    // Also listen to custom variant change events
    document.addEventListener('variant:changed', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateLowStockIndicatorFromVariant(event.detail.variant);
      }
    });
  }

  async updateLowStockIndicator() {
    const form = this.element.querySelector('form[data-product-form]');
    if (!form) return;

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!variantIdInput) return;

    const variantId = variantIdInput.value;

    try {
      // Fetch variant data from Shopify API
      const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
      if (!productHandle) return;

      const response = await fetch(`/products/${productHandle}.js`);
      const productData = await response.json();

      // Find the variant
      const variant = productData.variants.find(v => v.id.toString() === variantId.toString());
      if (variant) {
        this.updateLowStockIndicatorFromVariant(variant);
      }
    } catch (error) {
      console.error('Error fetching variant data:', error);
    }
  }

  updateLowStockIndicatorFromVariant(variant) {
    if (!this.lowStockElement) return;

    const inventory = variant.inventory_quantity;
    const available = variant.available;

    // Hide if out of stock or stock > threshold
    if (!available || inventory <= 0 || inventory > this.stockThreshold) {
      this.lowStockElement.style.display = 'none';
      return;
    }

    // Show and update
    this.lowStockElement.style.display = 'flex';

    // Update count
    const stockCountElement = this.lowStockElement.querySelector('[data-stock-count]');
    if (stockCountElement) {
      stockCountElement.textContent = inventory;
    }

    // Update bar width
    const stockBarElement = this.lowStockElement.querySelector('[data-stock-bar]');
    if (stockBarElement) {
      const percentage = Math.min(100, (inventory / this.stockThreshold) * 100);
      stockBarElement.style.width = `${percentage}%`;
    }
  }

  // ========================================
  // Anchor Links Methods
  // ========================================

  initializeAnchorLinks() {
    // Use event delegation on document for all anchor links
    document.addEventListener('click', (e) => {
      const link = e.target.closest('.product-form-pack-content__item-link');

      if (link) {
        const href = link.getAttribute('href');
        console.log('Link clicked, href:', href);

        // Check if it's an anchor link (starts with #)
        if (href && href.startsWith('#')) {
          e.preventDefault();
          e.stopPropagation();

          console.log('Anchor link detected, processing...');

          // Find the target element
          const targetId = href.substring(1);
          const targetElement = document.getElementById(targetId);

          console.log('Target ID:', targetId, 'Target element:', targetElement);

          if (targetElement) {
            console.log('Target element found:', targetElement);

            // Check if element is hidden
            const isHidden = window.getComputedStyle(targetElement).display === 'none';
            console.log('Is element hidden?', isHidden);

            // Get the section element - find the Shopify section wrapper
            let section = targetElement.closest('.shopify-section') ||
                         targetElement.closest('section') ||
                         targetElement;

            console.log('Section element:', section);
            console.log('Section display:', window.getComputedStyle(section).display);

            // If section is hidden, try to find a visible parent
            let scrollTarget = section;
            while (scrollTarget && window.getComputedStyle(scrollTarget).display === 'none') {
              scrollTarget = scrollTarget.parentElement;
              console.log('Trying parent element:', scrollTarget);
            }

            if (!scrollTarget || scrollTarget === document.body) {
              console.error('Could not find visible scroll target');
              return;
            }

            console.log('Final scroll target:', scrollTarget);
            console.log('Scroll target offsetTop:', scrollTarget.offsetTop);

            // Calculate header height dynamically
            const header = document.querySelector('.shopify-section-header-sticky') ||
                          document.querySelector('header') ||
                          document.querySelector('[data-header]');
            const headerHeight = header ? header.offsetHeight : 0;
            console.log('Header height:', headerHeight);

            // Calculate position with offset
            const targetPosition = scrollTarget.offsetTop - headerHeight - 20; // 20px extra padding
            console.log('Final scroll position:', targetPosition);

            // Scroll to target
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });

            // Optional: Add hash to URL without jumping
            setTimeout(() => {
              if (history.pushState) {
                history.pushState(null, null, href);
              }
            }, 100);
          } else {
            console.warn(`Target element with id "${targetId}" not found`);
          }
        }
      }
    });
  }

  // ========================================
  // Destroy Method
  // ========================================

  destroy() {
    // Cleanup sliders
    if (this.mainSlider) {
      this.mainSlider.destroy(true, true);
    }
    if (this.thumbsSlider) {
      this.thumbsSlider.destroy(true, true);
    }
    if (this.videoSlider) {
      this.videoSlider.destroy(true, true);
    }
    if (this.stickyDeliverySlider) {
      this.stickyDeliverySlider.destroy(true, true);
    }

    // Cleanup sticky bar observer
    if (this.stickyBarObserver) {
      this.stickyBarObserver.disconnect();
    }

    // Cleanup low stock observer
    if (this.lowStockObserver) {
      this.lowStockObserver.disconnect();
    }

    // Cleanup collapse
    this.collapseItems = [];

    // Cleanup upsells
    this.upsellsState = [];
  }
}

// Initialize all product templates
document.addEventListener('DOMContentLoaded', () => {
  const productTemplates = document.querySelectorAll('[data-section-type="w-product-template"]');
  const instances = [];

  productTemplates.forEach((element) => {
    const productTemplateElement = element.querySelector('.product-template');
    if (productTemplateElement) {
      const instance = new ProductTemplate(productTemplateElement);
      instances.push(instance);
    }
  });

  // Store instances for potential cleanup
  window.productTemplateInstances = instances;
});

// Handle Shopify section editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const productTemplateElement = event.target.querySelector('.product-template');
    if (productTemplateElement) {
      new ProductTemplate(productTemplateElement);
    }
  });

  document.addEventListener('shopify:section:unload', () => {
    if (window.productTemplateInstances) {
      window.productTemplateInstances.forEach((instance) => instance.destroy());
      window.productTemplateInstances = [];
    }
  });
}

// Expose class globally for dynamic initialization
window.ProductTemplate = ProductTemplate;
